type ProjectVersionReceipt = {
    tenantId?: string | null;
    tenant_id?: string | null;
    projectId?: string | null;
    project_id?: string | null;
    updatedAt?: string | null;
    updated_at?: string | null;
};

type NormalizedProjectVersionReceipt = {
    tenantId: string;
    projectId: string;
    updatedAt: string;
};

type VisibilitySource = {
    visibilityState?: string;
    addEventListener?: (type: string, callback: () => void) => void;
    removeEventListener?: (type: string, callback: () => void) => void;
};

type ConnectivitySource = {
    addEventListener?: (type: string, callback: () => void) => void;
    removeEventListener?: (type: string, callback: () => void) => void;
};

type ProjectRemoteVersionMonitorOptions = {
    tenantId: string;
    projectId: string;
    getBaselineUpdatedAt: () => string | null | undefined;
    fetchVersion: (
        projectId: string,
        options: { signal: AbortSignal },
    ) => Promise<ProjectVersionReceipt | null | undefined>;
    onRemoteVersion: (receipt: NormalizedProjectVersionReceipt) => void;
    onError?: (error: unknown) => void;
    intervalMs?: number;
    baseBackoffMs?: number;
    maxBackoffMs?: number;
    visibilitySource?: VisibilitySource | null;
    connectivitySource?: ConnectivitySource | null;
    isOnline?: () => boolean;
    /** True while one of THIS tab's own project writes is in flight. */
    isLocalWriteInFlight?: () => boolean;
    /** How long a newer version must stay newer before it is reported. */
    confirmDelayMs?: number;
};

function normalizedId(value: unknown): string {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizedIso(value: unknown): string | null {
    if (typeof value !== 'string' || !value.trim()) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function classifyProjectVersionReceipt(
    receipt: ProjectVersionReceipt | null | undefined,
    expected: {
        tenantId: string;
        projectId: string;
        baselineUpdatedAt: string | null | undefined;
    },
): { kind: 'remote'; receipt: NormalizedProjectVersionReceipt } | { kind: 'current' } | { kind: 'ignore' } {
    const tenantId = normalizedId(receipt?.tenantId ?? receipt?.tenant_id);
    const projectId = normalizedId(receipt?.projectId ?? receipt?.project_id);
    const updatedAt = normalizedIso(receipt?.updatedAt ?? receipt?.updated_at);
    const expectedTenantId = normalizedId(expected.tenantId);
    const expectedProjectId = normalizedId(expected.projectId);
    const baselineUpdatedAt = normalizedIso(expected.baselineUpdatedAt);

    // The authenticated endpoint is already RLS-scoped. These checks keep a
    // malformed, cached or accidentally cross-wired response from changing UI
    // state for a different tenant or Project.
    if (!tenantId || tenantId !== expectedTenantId) return { kind: 'ignore' };
    if (!projectId || projectId !== expectedProjectId) return { kind: 'ignore' };
    if (!updatedAt || !baselineUpdatedAt) return { kind: 'ignore' };

    const remoteMs = Date.parse(updatedAt);
    const baselineMs = Date.parse(baselineUpdatedAt);
    if (remoteMs <= baselineMs) return { kind: 'current' };
    return {
        kind: 'remote',
        receipt: { tenantId, projectId, updatedAt },
    };
}

/**
 * Poll only the lightweight authoritative Project version while the page is
 * visible. It never reloads or mutates Project state. Callers decide how to
 * present the receipt and the existing expectedUpdatedAt save guard remains
 * the final write boundary.
 */
export function createProjectRemoteVersionMonitor({
    tenantId,
    projectId,
    getBaselineUpdatedAt,
    fetchVersion,
    onRemoteVersion,
    onError = () => {},
    intervalMs = 45_000,
    baseBackoffMs = 30_000,
    maxBackoffMs = 5 * 60_000,
    visibilitySource = typeof document === 'undefined' ? null : document,
    connectivitySource = typeof window === 'undefined' ? null : window,
    isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false,
    isLocalWriteInFlight = () => false,
    confirmDelayMs = 5_000,
}: ProjectRemoteVersionMonitorOptions) {
    if (!normalizedId(tenantId) || !normalizedId(projectId)) {
        throw new TypeError('Project remote-version monitor requires tenantId and projectId');
    }
    if (typeof getBaselineUpdatedAt !== 'function'
        || typeof fetchVersion !== 'function'
        || typeof onRemoteVersion !== 'function') {
        throw new TypeError('Project remote-version monitor requires baseline, fetch and notification callbacks');
    }

    const interval = Math.max(15_000, Number(intervalMs) || 45_000);
    const baseBackoff = Math.max(5_000, Number(baseBackoffMs) || 30_000);
    const maxBackoff = Math.max(baseBackoff, Number(maxBackoffMs) || 5 * 60_000);
    const confirmDelay = Math.max(1_000, Number(confirmDelayMs) || 5_000);
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let checking = false;
    let pending = false;
    let consecutiveFailures = 0;
    let lastNotifiedUpdatedAt: string | null = null;
    // A version seen to be newer than the baseline, not yet confirmed.
    let unconfirmedRemoteUpdatedAt: string | null = null;

    const isVisible = () => visibilitySource?.visibilityState !== 'hidden';
    const canCheck = () => isVisible() && isOnline();
    const clearScheduled = () => {
        if (timer !== null) clearTimeout(timer);
        timer = null;
    };
    const schedule = (delay: number) => {
        if (stopped || !canCheck()) return;
        clearScheduled();
        timer = setTimeout(run, Math.max(0, delay));
    };

    async function run() {
        timer = null;
        if (stopped || checking || !canCheck()) return;
        checking = true;
        pending = false;
        let confirmSoon = false;
        const requestController = new AbortController();
        controller = requestController;
        try {
            const receipt = await fetchVersion(projectId, { signal: requestController.signal });
            if (stopped || requestController.signal.aborted) return;
            consecutiveFailures = 0;
            const result = classifyProjectVersionReceipt(receipt, {
                tenantId,
                projectId,
                baselineUpdatedAt: getBaselineUpdatedAt(),
            });
            if (result.kind === 'remote') {
                // Newer than the baseline is not yet proof of ANOTHER writer.
                // While this tab's own save is in flight the server already
                // holds the version that save produced, and the tab adopts it
                // only when its response arrives. Reporting that gap latched
                // 'a newer saved version needs review' and refused every later
                // save of a Quote Run (PROJECT_REMOTE_UPDATE_REVIEW_REQUIRED),
                // one device, one tab, nobody else signed in. It only shows
                // when the page is visible, and the window is widest on the
                // cloud, where a save takes seconds.
                //
                // So a newer version is reported only once it has been seen
                // twice, confirmDelay apart, with none of this tab's writes in
                // flight. A tab's own write has been adopted by then and reads
                // as current. Nothing is lost by waiting: the save guard
                // (expectedUpdatedAt) is the write boundary and stays fail
                // closed; this notice is advisory.
                const version = result.receipt.updatedAt;
                if (isLocalWriteInFlight() || unconfirmedRemoteUpdatedAt !== version) {
                    unconfirmedRemoteUpdatedAt = version;
                    confirmSoon = true;
                } else {
                    unconfirmedRemoteUpdatedAt = null;
                    if (lastNotifiedUpdatedAt !== version) {
                        lastNotifiedUpdatedAt = version;
                        onRemoteVersion(result.receipt);
                    }
                }
            } else {
                unconfirmedRemoteUpdatedAt = null;
                if (result.kind === 'current') lastNotifiedUpdatedAt = null;
            }
        } catch (error) {
            if (!requestController.signal.aborted && !stopped) {
                consecutiveFailures += 1;
                onError(error);
            }
        } finally {
            if (controller === requestController) controller = null;
            checking = false;
            if (stopped || !canCheck()) return;
            if (confirmSoon) {
                // Before 'pending': an immediate re-check would confirm the very
                // gap this delay exists to let close.
                schedule(confirmDelay);
            } else if (pending) {
                schedule(0);
            } else if (consecutiveFailures > 0) {
                schedule(Math.min(
                    maxBackoff,
                    baseBackoff * (2 ** Math.max(0, consecutiveFailures - 1)),
                ));
            } else {
                schedule(interval);
            }
        }
    }

    const requestCheck = () => {
        if (stopped) return;
        pending = true;
        if (!canCheck() || checking) return;
        schedule(0);
    };

    const onVisibilityChange = () => {
        if (stopped) return;
        if (!canCheck()) {
            pending = true;
            clearScheduled();
            controller?.abort('page-inactive');
            return;
        }
        requestCheck();
    };

    const onConnectivityChange = () => {
        if (stopped) return;
        if (!canCheck()) {
            pending = true;
            clearScheduled();
            controller?.abort('page-offline');
            return;
        }
        requestCheck();
    };

    visibilitySource?.addEventListener?.('visibilitychange', onVisibilityChange);
    connectivitySource?.addEventListener?.('online', onConnectivityChange);
    connectivitySource?.addEventListener?.('offline', onConnectivityChange);
    requestCheck();

    return {
        requestCheck,
        stop() {
            if (stopped) return;
            stopped = true;
            pending = false;
            clearScheduled();
            controller?.abort('monitor-stopped');
            controller = null;
            visibilitySource?.removeEventListener?.('visibilitychange', onVisibilityChange);
            connectivitySource?.removeEventListener?.('online', onConnectivityChange);
            connectivitySource?.removeEventListener?.('offline', onConnectivityChange);
        },
    };
}
